import React, { useState, useEffect, useContext } from "react";
import { useParams, useLocation, useHistory, Link } from "react-router-dom";
import { NotificationAlertContext } from "contexts/notificationAlertContext";
import { API, graphqlOperation } from "aws-amplify";
import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination.js";
import { getMirthResendTaskDetails, getMirthMessageDetails } from "graphql/queries.js";

// react-bootstrap components
import {
    Button,
    Container,
    Row,
    Col,
    Spinner,
    Card,
    Table,
} from "react-bootstrap";
import Select from "react-select";
import {
  MultiValueWithCustomTooltip,
  customEllipsisMultiSelectStyles,
} from "components/Custom/CustomMultiSelect.js";
import { convertDateToLocaleString } from "utilities/dateUtilities";
import { Modal, ModalBody, ModalFooter } from "reactstrap";
import CustomTooltip from "components/Custom/CustomTooltip";
import { copyToClipboard } from "utilities/commonUtilities.js";

function ResendTaskDetails() {
    const { cloudTaskId } = useParams();
    const location = useLocation();
    const history = useHistory();
    const notify = useContext(NotificationAlertContext);
    // Get parameters from navigation state or URL params
    const searchParams = new URLSearchParams(location.search);
    const [taskData, setTaskData] = useState({
        task_id: "",
        environment_name: "",
        client_name: "",
        channel_name: "",
        channel_id: "",
        sent_total: 0,
        error_total: 0,
        total_count: 0,
        messages: []
    });
    const [messageData, setMessageData] = useState({
        task_id: "",
        client_name: "",
        client_environment_name: "",
        channel_name: "",
        task_type: "",
        message_status: "",
        mc_message_status: "",
        message_id: "",
        connectors: []
    });
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);

    const [showMessageDetailsModal, setShowMessageDetailsModal] = useState(false);
    const [isMessageLoading, setIsMessageLoading] = useState(false);

    // Extract required parameters with fallback priority
    const clientEnvId = searchParams.get('clientEnvironmentId') || "";
    const taskDateRollup = searchParams.get('dateRollup') || "";

    // Pagination states
    const [currentLimit, setCurrentLimit] = useState(50);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [ascOrDescValue, setAscOrDescValue] = useState("ASC");
    const [sortValue, setSortValue] = useState("Message Id");
    const [isFirstQuery, setIsFirstQuery] = useState(true);

    // Filter state
    const [statusFilter, setStatusFilter] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    // Status filter options
    const statusFilterOptions = [
        { value: "SUCCESS", label: "Success (SENT, FILTERED, QUEUED)" },
        { value: "ERROR", label: "Error" },
    ];

    const numberOfRowsData = [25, 50, 100];

    const columnMapping = {
        "Message Id": "MSG_ID",
        "Status": "MSG_STATUS",
        "Type": "message_type",
        "Date Received": "DATE_RECEIVED",
        "Date Completed": "DATE_COMPLETED",
    };

    const setStateForPagnination = async (key, value) => {
        if (key === "currentLimit") {
            setCurrentLimit(value);
        } else if (key === "offset") {
            setCurrentOffset(value);
        } else if (key === "ascOrDesc") {
            setAscOrDescValue(value);
        } else {
            setSortValue(value);
        }
    };

    const noMargin = {
        margin: 0,
    };

    // Handle filter change
    const handleStatusFilterChange = (selectedOptions) => {
        setStatusFilter(selectedOptions || []);
    };

    // Handle search button click
    const handleSearch = async () => {
        setCurrentOffset(0);
        setIsFirstQuery(true); // Reset to get accurate total for new filter
        
        // Refetch data with new filter
        const filterObj = {
            offSet: 0,
            limit: currentLimit,
            sortFilter: columnMapping[sortValue],
            sortType: ascOrDescValue,
        };
        await fetchResendTaskDetails(filterObj);
    };

    // Fetch task details using GraphQL
    const fetchResendTaskDetails = async (variables = {}, isCSVDownload = false) => {
        try {
            setLoading(true);

            // Prepare status filter for API - handle both array and string inputs
            let msgStatuses = [];
            msgStatuses = statusFilter.map(filter => filter.value);

            const response = await API.graphql(
                graphqlOperation(getMirthResendTaskDetails, {
                    clientEnvironmentId: clientEnvId,
                    cloudTaskId: cloudTaskId,
                    dateRollup: taskDateRollup,
                    limit: variables.limit || currentLimit,
                    offset: variables.offSet || currentOffset,
                    accurateTotal: isFirstQuery,
                    msgResendStatuses: msgStatuses,
                    sortFilter: variables.sortFilter || columnMapping[sortValue],
                    sortType: variables.sortType || ascOrDescValue
                })
            );

            const result = response.data.getMirthResendTaskDetails;

            if (result.body) {
                setTaskData(result.body);
                // Since filtering is now done server-side, use all messages
                setFilteredData(result.body.messages || []);

                // Set isFirstQuery to false after first successful API call
                if (isFirstQuery) {
                    setTotalCount(result.body.total_count || 0);
                    setIsFirstQuery(false);
                }

                if (!isCSVDownload) {
                    notify("success", "Task details loaded successfully");
                } else {
                    const filter = {};
                    const messageDataForCSV = _.get(
                        response,
                        "data.getMirthResendTaskDetails.body.messages"
                    );
                    return { messageDataForCSV, filter };
                }
                return true;
            } else {
                throw new Error(result.error || result.message || "Failed to fetch task details");
            }
        } catch (error) {
            console.error("Error fetching task details:", error);
            history.push("/admin/mirth-tasks");
            notify("danger", `Failed to load task details: ${error.message}`);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const fetchMessageDetails = async (message_id) => {
        try {
            setIsMessageLoading(true);

            const response = await API.graphql(
                graphqlOperation(getMirthMessageDetails, {
                    clientEnvironmentId: clientEnvId,
                    cloudTaskId: cloudTaskId,
                    dateRollup: taskDateRollup,
                    channelId: taskData.channel_id,
                    messageId: parseInt(message_id)
                })
            );

            const result = response.data.getMirthMessageDetails;

            if (result.success && result.body) {
                // Transform the API response to match the UI structure
                const transformedData = {
                    task_id: result.body.task_id,
                    client_name: result.body.client_name || "Unknown Client",
                    client_environment_name: result.body.environment_name || "Unknown Environment",
                    channel_name: result.body.channel_name || "Unknown Channel",
                    task_type: "Message Resend",
                    message_status: result.body.message_status,
                    mc_message_status: result.body.mc_message_status,
                    message_id: result.body.message_id,
                    original_message_id: result.body.original_message_id,
                    connectors: result.body.connector_details?.map((connector, index) => ({
                        connector_id: connector.connector_id,
                        connector_status: connector.connector_status,
                        connector_error_text: connector.connector_error_text || ""
                    })) || []
                };

                setMessageData(transformedData);
                notify("success", "Message details loaded successfully");
                return true;
            } else {
                notify("danger", result.message || "Failed to load message details");
                return false;
            }
        } catch (error) {
            console.error("Error fetching message details:", error);
            notify("danger", `Failed to load message details: ${error.message}`);
            return false;
        } finally {
            setIsMessageLoading(false);
        }
    };

    useEffect(() => {
        async function fetchData() {
            if (clientEnvId && cloudTaskId && taskDateRollup) {
                const filterObj = {
                    offSet: 0,
                    limit: currentLimit,
                    sortFilter: columnMapping[sortValue],
                    sortType: ascOrDescValue,
                    
                };
                setCurrentOffset(0);
                const isSuccess = await fetchResendTaskDetails(filterObj);
                if (!isSuccess) {
                    notify("danger");
                }
            } else {
                notify("danger", "Missing required parameters for task details");
                history.push("/admin/mirth-tasks");
            }
        }
        fetchData();
    }, [clientEnvId, cloudTaskId, taskDateRollup]);

    if (loading) return <Spinner animation="grow" />;

    return (
        <>
            <Container fluid>
                <Row>
                    <Col>
                        <h2>Mirth Task - Resend Details</h2>
                    </Col>
                </Row>
                {taskData.task_id && (
                    <Row>
                        <Col md="12">
                            <h5>Task Id: {taskData.task_id}</h5>
                            <h5>Client Name: {taskData.client_name}</h5>
                            <h5>Environment Name: {taskData.environment_name}</h5>
                            <h5>Channel Name: {taskData.channel_name}</h5>
                            <h5>Sent: {taskData.sent_total}</h5>
                            <h5>Error: {taskData.error_total}</h5>
                        </Col>
                    </Row>
                )}

                <Row>
                    <Col md="12">
                        <Card>
                            <Card.Body>
                                <Row className="align-items-end">
                                    <Col md="4" sm="12">
                                        <label>Message Status Filter</label>
                                        <Select
                                            className="react-select primary"
                                            classNamePrefix="react-select"
                                            value={statusFilter}
                                            onChange={handleStatusFilterChange}
                                            options={statusFilterOptions}
                                            placeholder="Select Status(es)"
                                            isMulti
                                            styles={customEllipsisMultiSelectStyles}
                                            components={{ MultiValue: MultiValueWithCustomTooltip }}
                                            closeMenuOnSelect={false}
                                        />
                                    </Col>
                                    <Col md="2" sm="12" className="d-flex align-items-end">
                                        <Button
                                            className="btn-fill"
                                            color="primary"
                                            type="button"
                                            onClick={handleSearch}
                                        >
                                            Search
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col md="12">
                        {taskData && (
                            <ReactTable
                                data={filteredData || []}
                                totalCount={totalCount}
                                functionCallBack={fetchResendTaskDetails}
                                setStateForPagnination={setStateForPagnination}
                                currentLimit={currentLimit}
                                currentOffset={currentOffset}
                                ascOrDescValue={ascOrDescValue}
                                sortValue={sortValue}
                                numberOfRowsData={numberOfRowsData}
                                columnMapping={columnMapping}
                                tableName="Task Details"
                                CSVHeaders={[
                                    { key: "message_id", label: "Message Id" },
                                    { key: "original_message_id", label: "Original Message Id" },
                                    { key: "mc_message_status", label: "MC Msg Outcome" },
                                    { key: "message_type", label: "Type" },
                                    { key: "message_date_received", label: "Date Received" },
                                    { key: "message_date_completed", label: "Date Completed" },
                                ]}
                                CSVFileName="Task_Details_Export"
                                notify={notify}
                                columns={[
                                    {
                                        Header: "Message Id",
                                        accessor: "message_id",
                                        filterable: false,
                                    },
                                    {
                                        Header: "Original Message Id",
                                        accessor: "original_message_id",
                                        filterable: false,
                                        sortable: false,
                                    },
                                    {
                                        Header: "MC Msg Outcome",
                                        accessor: "mc_message_status",
                                        filterable: false,
                                        sortable: false,
                                    },
                                    {
                                        Header: "Type",
                                        accessor: "message_type",
                                        filterable: false,
                                    },
                                    {
                                        Header: "Details",
                                        Cell: (cell) => {
                                            const row = cell.row.original;
                                            return (
                                                <a
                                                    href="#"
                                                    className="link-button"
                                                    onClick={async (e) => {
                                                        e.preventDefault();
                                                        setMessageData({
                                                            task_id: "",
                                                            client_name: "",
                                                            client_environment_name: "",
                                                            channel_name: "",
                                                            task_type: "",
                                                            message_status: "",
                                                            mc_message_status: "",
                                                            message_id: "",
                                                            connectors: []
                                                        });
                                                        setShowMessageDetailsModal(true);
                                                        fetchMessageDetails(row.message_id);
                                                    }}
                                                >
                                                View Message Details
                                                </a>
                                            );
                                        },
                                        filterable: false,
                                        sortable: false,
                                    },
                                    {
                                        Header: "Date Received",
                                        Cell: (cell) => {
                                            const row = cell.row.original;
                                            return convertDateToLocaleString(row.message_date_received);
                                        },
                                        filterable: false,
                                        showTooltip: true,
                                    },
                                    {
                                        Header: "Date Completed",
                                        Cell: (cell) => {
                                            const row = cell.row.original;
                                            return convertDateToLocaleString(row.message_date_completed);
                                        },
                                        filterable: false,
                                        showTooltip: true,
                                    },
                                ]}
                                hidePagination={false}
                            />
                        )}
                    </Col>
                </Row>
                <Modal
                    toggle={() => setShowMessageDetailsModal(!showMessageDetailsModal)}
                    size="xl"
                    isOpen={showMessageDetailsModal}
                    scrollable={true}
                    >
                    <ModalBody style={{ minHeight: "100px" }}>
                        {isMessageLoading ? (
                        <Spinner animation="grow" />
                        ) : (
                            <>
                            <Row>
                                <Col>
                                    <h2>Message Details</h2>
                                </Col>
                            </Row>
                            <Card id="client-details-card">
                                <Card.Body>
                                    <Row>
                                        <Col md="12">
                                            <h3>Client Details</h3>
                                            <p style={noMargin}>Task Id: {messageData.task_id}</p>
                                            <p style={noMargin}>Task Type: {messageData.task_type}</p>
                                            <p style={noMargin}>Client Name: {messageData.client_name}</p>
                                            <p style={noMargin}>Client Environment Name: {messageData.client_environment_name}</p>
                                            <p style={noMargin}>Channel Name: {messageData.channel_name}</p>
                                            <p style={noMargin}>Message Id: {messageData.message_id}</p>
                                            <p style={noMargin}>Original Message Id: {messageData.original_message_id}</p>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                            <Card>
                                <Card.Body>
                                    <Row>
                                        <Col md="12" id="connector-details">
                                            <h3>Connector Details</h3>
                                            {messageData.connectors && messageData.connectors.length > 0 && (
                                                <>
                                                    <Table className="table-hover table-striped w-full">
                                                        <thead>
                                                            <tr>
                                                                <th>Connector ID</th>
                                                                <th>Connector Message Status</th>
                                                                <th>Connector Error Text</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {messageData.connectors.map((connector, key) => {
                                                                return (
                                                                    <tr key={key}>
                                                                        <td>{connector.connector_id}</td>
                                                                        <td>{connector.connector_status}</td>
                                                                        <td>
                                                                            {connector.connector_error_text && (
                                                                                <CustomTooltip
                                                                                    id={`connector-error-tooltip-${connector.connector_id}`}
                                                                                    placement="right"
                                                                                    value={connector.connector_error_text}
                                                                                >
                                                                                    {connector.connector_error_text}
                                                                                </CustomTooltip>
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            {connector.connector_status === "ERROR" && (
                                                                                <CustomTooltip
                                                                                    id={`copy-error-${connector.connector_id}`}
                                                                                    placement="right"
                                                                                    value="Copy connector error text"
                                                                                >
                                                                                    <Button
                                                                                        variant="primary"
                                                                                        className="btn-outline"
                                                                                        onClick={() => {
                                                                                            copyToClipboard(connector.connector_error_text);
                                                                                        }}
                                                                                    >
                                                                                        <i className="fas fa-copy"></i>
                                                                                    </Button>
                                                                                </CustomTooltip>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </>
                                            )}
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" disabled={isMessageLoading} onClick={() => setShowMessageDetailsModal(false)}>
                        Close
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </>
    );
}

export default ResendTaskDetails;
